// src/utils/helpers/handleDownload.js

import { utils, writeFile } from 'xlsx';
import { useCallback } from 'react';
// --- Utility Function Imports ---
// Ensure these paths are correct relative to your project structure
import determineStage from '@/utils/DetermineStage';
import checkToFinanceApprovalStatus from '@/utils/Sort/checkTOFinanceApprovalStatus';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import getNetRecurringBenefitCategory from '@/utils/Sort/getNetRecurringBenefitCatagory';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import L0PlanDate from '@/utils/Sort/checkDate/l0ActualDate';
import L1PlanDate from '@/utils/Sort/checkDate/L1PlanDate';
import L2ActualDate from '@/utils/Sort/checkDate/l2ActualDate';
import L2PlanDate from '@/utils/Sort/checkDate/l2PlanDate';
import L3ActualDate from '@/utils/Sort/checkDate/l3ActualDate';
import L4ActualDate from '@/utils/Sort/checkDate/l4ActualDate';
import L5ActualDate from '@/utils/Sort/checkDate/l5ActualDate';
import L3PlanDate from '@/utils/Sort/checkDate/l3PlanDate';
import L4PlanDate from '@/utils/Sort/checkDate/l4PlanDate';
import L5PlanDate from '@/utils/Sort/checkDate/l5PlanDate';
import LLatestActualDate from '@/utils/Sort/checkDate/LLatestActualDate';
import SubmittedForL1PlanDate from '@/utils/Sort/checkDate/submittedForL1ApprovalPlanDate';
import SubmittedForL2PlanDate from '@/utils/Sort/checkDate/submittedForL2ApprovalPlanDate';
import SubmittedForL3PlanDate from '@/utils/Sort/checkDate/submittedForL3ApprovalPlanDate';
import SubmittedForL4PlanDate from '@/utils/Sort/checkDate/submittedForL4ApprovalPlanDate';
import SubmittedForL5PlanDate from '@/utils/Sort/checkDate/submittedForL5ApprovalPlanDate';
import getNetRecurringBenefitChangeCategoryL0Plus from '@/utils/color/getNetRecurringBenefitCatagoryL0Plus';
import getNetRecurringBenefitChangeCategoryL1Plus from '@/utils/color/getNetRecurringBenefitCatagoryL1Plus';
import getNetRecurringBenefitChangeCategoryL2Plus from '@/utils/color/getNetRecurringBenefitCatagoryL2Plus';
import getNetRecurringBenefitChangeCategoryL3Plus from '@/utils/color/getNetRecurringBenefitCatagoryL3Plus';
import getNetRecurringBenefitChangeCategoryL4Plus from '@/utils/color/getNetRecurringBenefitCatagoryL4Plus';
import getComplianceStatus from '@/utils/Sort/complianceCheck';
import sortInitiativesByActualImpact from '@/utils/Sort/sortByActualImpact';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import sortInitiativesByActuals from '@/utils/Sort/sortByActual';
import getImplementationCostCatagory from '@/utils/Sort/getImplementationCostCatagory';
import { determinePlanningSummary } from '@/utils/planningSummary'; // Ensure this is imported for workstream actions
import calculateNetRecurringBenefit from '@/utils/calculation/calculationRecurringBenefit';
import calculateOneTimeBenefit from '@/utils/calculation/calculateOneTimeBenefit';
import calculateOneTimeImplementationCost from '@/utils/calculation/calculateOneTimeImplementationCost';
import { getMilestoneBinValue } from '@/utils/helpers/getMilestoneBinValue';

// --- Column Definition Imports ---
import initialColumns from '@/utils/column/initialColumns'; // The initiative columns you provided
import milestoneInitialColumns from '@/utils/column/milestoneInitialColumn';
import actionItemInitialColumn from '@/utils/column/actionItemInitialColumn'; // Your new action item columns
import { getCancellationDate, getQualitativeKPI, L1OriginalPlanDate, L2OriginalPlanDate, L3OriginalPlanDate, L4OriginalPlanDate, L5OriginalPlanDate } from '@/utils/Sort/checkDate/getDate';
import groupInitiativesByYearActual from '@/utils/Sort/groupInitiativesByYearActual';
import getMilestonePlanningSummary from '@/utils/filters/getPlanningSummary';
import getSimplifiedPlanningSummary from '@/components/Kanban/Workbook/getMilestonePlanningSummary';
import filterMilestoneByPlanningSummary from '@/utils/filters/filterMilestonesByPlanningSummary';
import planningSummarySimplified from '@/utils/planningSummarySimplified';

/**
 * Custom React hook for downloading various data sets into a single Excel file with multiple sheets.
 * This hook encapsulates the data processing and Excel generation logic.
 *
 * @param {Array} users - Array of user objects (allUserData).
 * @param {Array} initiatives - Array of initiative objects (allInitiativeData).
 * @param {Array} subworkstreams - Array of Subworkstream documents.
 * @param {Array} workstreams - Array of parent Workstream documents.
 * @param {Array} actionItems - Array of workstream action item objects.
 * @param {Array} historyData - Array of history objects (allHistoryData, primarily for approval status checks).
 * @param {string} selectedRange - The currently selected date range for filtering.
 */
export const useDownloadData = (users, initiatives, subworkstreams, workstreams, actionItems, historyData, selectedRange) => {

  const handleDownload = useCallback(async () => {
    try {
      const processPersonArray = (arr) => {
        if (arr && Array.isArray(arr) && arr.length > 0 && arr[0]) {
          return `${arr[0].firstName || ''} ${arr[0].lastName || ''}`.trim();
        }
        return '';
      };

      const processSubworkstreamArray = (arr) => {
        if (arr && Array.isArray(arr) && arr.length > 0 && arr[0]) {
          return arr[0].subworkstreamName || '';
        }
        return '';
      };

      const processWorkstreamArray = (arr) => {
        if (arr && Array.isArray(arr) && arr.length > 0 && arr[0]) {
          return arr[0].workstreamName || '';
        }
        return '';
      };

      const processCrossFunctionalWorkstreamArray = (arr) => {
        if (arr && Array.isArray(arr) && arr.length > 0 && arr[0]) {
          return arr[0].subworkstreamName || '';
        }
        return '';
      };

      // ===== WORKING DATE FORMATTING FUNCTIONS =====
      const applyDateFormats = (worksheet) => {
        if (!worksheet || !worksheet['!ref']) return;

        const range = utils.decode_range(worksheet['!ref']);

        // Get header row to identify date columns
        const headers = [];
        for (let C = 0; C <= range.e.c; C++) {
          const headerCell = worksheet[utils.encode_cell({ r: 0, c: C })];
          headers[C] = headerCell?.v?.toString().toLowerCase() || '';
        }

        // Keywords that indicate a column contains dates
        const dateKeywords = [
          'date', 'planned', 'actual', 'original', 'submitted', 'created', 'updated',
          'cancellation', 'start', 'completion', 'due', 'L0', 'L1', 'L2', 'L3', 'L4', 'L5'
        ];

        // Check each column
        for (let C = 0; C <= range.e.c; C++) {
          // Check if this column might be a date column
          const isDateColumn = dateKeywords.some(keyword => headers[C].includes(keyword));

          if (isDateColumn) {
            for (let R = 1; R <= range.e.r; R++) {
              const cellAddress = utils.encode_cell({ r: R, c: C });
              const cell = worksheet[cellAddress];

              // If it's a number that could be a date (between 40000-50000 for recent years)
              if (cell && cell.t === 'n' && cell.v > 40000 && cell.v < 50000) {
                cell.z = 'yyyy-mm-dd'; // Format as date without time
              }
            }
          }
        }
      };


      // In your download handler, replace the formatDateSafely function
      const formatDateSafely = (dateInput) => {
        if (!dateInput) return null;

        try {
          let d;
          if (dateInput instanceof Date) {
            d = dateInput;
          } else {
            d = new Date(dateInput);
          }

          // Check if date is valid
          if (isNaN(d.getTime())) return null;

          // Check if it's the epoch (1970-01-01) - return null to keep cell blank
          if (d.getUTCFullYear() === 1970 && d.getUTCMonth() === 0 && d.getUTCDate() === 1) {
            return null;
          }

          // Use UTC components to calculate Excel serial number
          const year = d.getUTCFullYear();
          const month = d.getUTCMonth();
          const day = d.getUTCDate();

          // Calculate Excel serial number using UTC
          // Formula: = (date - DATE(1900,1,1)) + 2 (for Excel's leap year bug)
          const excelEpoch = new Date(Date.UTC(1900, 0, 1));
          const currentDate = new Date(Date.UTC(year, month, day));
          const diffTime = currentDate.getTime() - excelEpoch.getTime();
          const diffDays = diffTime / (1000 * 60 * 60 * 24);

          // Excel incorrectly treats 1900 as a leap year, so add 2 for dates after 1900-02-28
          return diffDays + 2;
        } catch (error) {
          console.error('Error formatting date:', error);
          return null;
        }
      };      // ===== END OF WORKING DATE FORMATTING FUNCTIONS =====

      // Helper to find parent initiative for sub-documents (milestones, impacts, KPIs, etc.)
      const findParentInitiative = (childItem, initiativesArray = initiatives) => {
        // Check if it's a milestone
        if (childItem.milestoneNumber && childItem.name) {
          return initiativesArray.find(i => i.milestones?.some(m => m._id?.toString() === childItem._id?.toString()));
        }
        // Check if it's an impact
        if (childItem.impactNumber) {
          return initiativesArray.find(i => i.impact?.some(imp => imp._id?.toString() === childItem._id?.toString()));
        }
        // Check if it's a KPI
        if (childItem.KPINumber) {
          return initiativesArray.find(i => i.KPI?.some(kpi => kpi._id?.toString() === childItem._id?.toString()));
        }
        // Check if it's an attachment
        if (childItem.attachmentType || childItem.url || typeof childItem === 'string') { // handle attachments that are just URLs (strings)
          return initiativesArray.find(i => i.attachment?.some(att =>
            (typeof att === 'object' && att._id?.toString() === childItem._id?.toString()) ||
            (typeof att === 'string' && att === childItem)
          ));
        }
        // Check if it's a timeline entry
        if (childItem.stage && childItem.date) {
          return initiativesArray.find(i => i.timeline?.some(tl => tl._id?.toString() === childItem._id?.toString()));
        }
        // Check if it's a business case
        if (childItem.businessCaseName) { // Assuming businessCase has a name field
          return initiativesArray.find(i => i.businessCase?.some(bc => bc._id?.toString() === childItem._id?.toString()));
        }
        return null;
      };

      // Helper to get user from history for created/updated by fields for Initiatives & Milestones
      const getLatestUserFromHistory = (item, actionSubstring, itemIdentifierProperty, allHistoryData, allUsersData) => {
        let historyEntriesToSearch = [];
        let identifierValue = item[itemIdentifierProperty]; // e.g., initiative.initiativeNumber, milestone.name

        // If the item itself has a history (e.g., Initiative)
        if (item.history && Array.isArray(item.history)) {
          historyEntriesToSearch = item.history;
        } else {
          // For sub-documents (milestones), find the parent initiative and use its history
          const parentInitiative = findParentInitiative(item, initiatives);
          historyEntriesToSearch = parentInitiative?.history || [];
        }

        if (!historyEntriesToSearch.length) return 'N/A';

        const matches = historyEntriesToSearch.filter(entry => {
          const actionText = entry.action || '';
          if (actionSubstring === 'created') {
            // Specific checks for creation actions based on common patterns
            if (item.milestoneNumber && actionText.includes(`Milestone #${item.name} created`)) return true;
            if (item.actionItemNumber && actionText.includes(`Workstream action #${item.actionItemNumber} created`)) return true;
            if (item.initiativeNumber && actionText.includes(`Initiative ${item.initiativeNumber} created`)) return true;
            // Fallback for general creation text if specific pattern not found
            return actionText.includes('created') && actionText.includes(identifierValue);
          } else if (actionSubstring === 'updated') {
            // Specific checks for update actions based on common patterns
            if (item.milestoneNumber && actionText.includes(`Milestone with Number ${item.milestoneNumber} has been updated.`)) return true;
            if (item.actionItemNumber && actionText.includes(`Workstream action ${item.actionItemNumber} is updated`)) return true;
            if (item.initiativeNumber && actionText.includes(`Initiative ${item.initiativeNumber} has been updated.`)) return true;
            // Fallback for general update text if specific pattern not found
            return actionText.includes('updated') && actionText.includes(identifierValue);
          }
          return false;
        }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Sort by most recent

        const userFound = allUsersData?.find(u => u._id === matches[0]?.user);
        return userFound ? `${userFound.firstName || ''} ${userFound.lastName || ''}`.trim() : 'N/A';
      };

      const getLatestUpdateEvent = (actionItem) => {
        // Check for the 'is updated' event and sort to find the most recent one.
        if (!actionItem.activity || actionItem.activity.length === 0) {
          return null;
        }
        const updateEvents = actionItem.activity.filter(event =>
          event.event?.includes(`Workstream action ${actionItem.actionItemNumber} is updated`)
        );
        // Sort events by date in descending order to get the latest one.
        updateEvents.sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));
        return updateEvents[0] || null;
      };

      const wb = utils.book_new();
      // Define start and end years for dynamic headers (consistent across Impact and KPI)
      const dataStartYear = 2023;
      const dataEndYear = 2028; // Data goes up to June 2028
      // Generate months from July 2023 to June 2028 for monthly data (60 months total)
      const monthsOrder = [];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      for (let year = dataStartYear; year <= dataEndYear; year++) {
        let currentMonthIndex = (year === dataStartYear) ? 6 : 0; // Start from July for 2023 (index 6), Jan for others
        let lastMonthIndex = (year === dataEndYear) ? 5 : 11; // End at June for 2028 (index 5), Dec for others

        for (let i = currentMonthIndex; i <= lastMonthIndex; i++) {
          monthsOrder.push({ name: monthNames[i], year: year });
        }
      }

      const yearsForHeaders = [];
      for (let i = 0; i < 5; i++) {
        yearsForHeaders.push(dataStartYear + i);
      }

      const quartersForHeaders = [];
      const quarterPrefixes = ['Q1', 'Q2', 'Q3', 'Q4'];
      let currentQuarterYear = dataStartYear;
      for (let i = 0; i < 20; i++) {
        quartersForHeaders.push(`${quarterPrefixes[i % 4]}-${currentQuarterYear}`);
        if ((i + 1) % 4 === 0) { // Increment year after every 4 quarters
          currentQuarterYear++;
        }
      }

      const maxMonthlyDataPoints = 60; // Assuming 60 months based on your model's default Array(60)
      const dynamicMonths = monthsOrder.slice(0, maxMonthlyDataPoints);

      // --- 1. Users Sheet ---
      if (users && users.length > 0) {
        const usersSheetData = users.map((user) => {
          const { _id, subworkstream, workstream, Delegator, Delegatee, role, ...rest } = user;
          const subworkstreamName = subworkstream ? subworkstream.subworkstreamName : '';
          const workstreamName = workstream ? workstream.workstreamName : '';
          const delegatorUser = users.find((u) => u._id === Delegator);
          const delegateeUser = users.find((u) => u._id === Delegatee);
          const delegatorName = delegatorUser ? `${delegatorUser.firstName || ''} ${delegatorUser.lastName || ''}`.trim() : '';
          const delegateeName = delegateeUser ? `${delegateeUser.firstName || ''} ${delegateeUser.lastName || ''}`.trim() : '';
          const roleName = role ? role.name : '';

          return {
            ...rest,
            role: roleName,
            subworkstream: subworkstreamName,
            workstream: workstreamName,
            Delegator: delegatorName,
            Delegatee: delegateeName,
          };
        });
        utils.book_append_sheet(wb, utils.json_to_sheet(usersSheetData), 'Users');
      } else {
        console.warn("No users data to download.");
      }

      // --- 2. Initiatives Sheet (All Columns from initialColumns) ---
      if (initiatives && initiatives.length > 0) {
        const initiativesSheetData = initiatives.map(initiative => {
          const rowData = {};
          initialColumns.forEach(columnDef => {
            switch (columnDef.key) {
              case 'initiativeNumber': rowData[columnDef.title] = parseInt(initiative.initiativeNumber) || ''; break;
              case 'initiativeName': rowData[columnDef.title] = initiative.initiativeName || ''; break;
              case 'stage': rowData[columnDef.title] = determineStage(initiative); break;
              case 'stageSimplified': rowData[columnDef.title] = initiative.stage; break;
              case 'weeklyStatus': rowData[columnDef.title] = initiative.weeklyStatus || ''; break;
              case 'initiativeOwnerInfo': rowData[columnDef.title] = processPersonArray(initiative.initiativeOwnerInfo); break;
              case 'workstreamSponsorWorkstream': rowData[columnDef.title] = processWorkstreamArray(initiative.workstreamSponsorWorkstream); break;
              case 'sectorInfo':
                // Sector information - handle both array and object formats
                if (initiative.sectorInfo) {
                  if (Array.isArray(initiative.sectorInfo) && initiative.sectorInfo.length > 0) {
                    rowData[columnDef.title] = initiative.sectorInfo[0]?.sectorName || '';
                  } else if (typeof initiative.sectorInfo === 'object') {
                    rowData[columnDef.title] = initiative.sectorInfo.sectorName || '';
                  }
                } else {
                  rowData[columnDef.title] = '';
                }
                break;

              case 'subsectorInfo':
                // Subsector information - handle both array and object formats
                if (initiative.subsectorInfo) {
                  if (Array.isArray(initiative.subsectorInfo) && initiative.subsectorInfo.length > 0) {
                    rowData[columnDef.title] = initiative.subsectorInfo[0]?.subsectorName || '';
                  } else if (typeof initiative.subsectorInfo === 'object') {
                    rowData[columnDef.title] = initiative.subsectorInfo.subsectorName || '';
                  }
                } else {
                  rowData[columnDef.title] = '';
                }
                break;

              case 'initiativeCategory':
                // Segment - according to your note, initiativeCategory is the segment
                rowData[columnDef.title] = initiative.initiativeCategory || '';
                break;

              case 'segment':
                // Subsegment - according to your note, segment is the subsegment
                rowData[columnDef.title] = initiative.segment || '';
                break;

              case 'crossFunctionalSupportWorkstreamInfo': rowData[columnDef.title] = processCrossFunctionalWorkstreamArray(initiative.crossFunctionalSupportWorkstreamInfo); break;
              case 'strategyInfo':
                const strategyNamesString = initiative.strategyInfo
                  ?.map((s) => s.strategyName)
                  .join(', ');
                rowData[columnDef.title] = strategyNamesString || 'No strategy'; break;
              case 'initiativeOwnerBackupInfo': rowData[columnDef.title] = processPersonArray(initiative.initiativeOwnerBackupInfo); break;
              case 'AllowOnlyTo': rowData[columnDef.title] = initiative.access?.map(u => `${u.firstName || ''} ${u.lastName || ''}`).filter(Boolean).join(', ') || ''; break;
              case 'hasFinancialImpact':
                rowData[columnDef.title] = initiative.hasFinancialImpact === true ? 'Yes' : 'No';
                break;
              case 'forSpecificCustomer':
                rowData[columnDef.title] = initiative.forSpecificCustomer === true ? 'Yes' : 'No'; break;
              case 'segmentalPBT':
                rowData[columnDef.title] = initiative.segmentalPBT ? 'Yes' : 'No';
                break;
              case 'priority':
                rowData[columnDef.title] = initiative.priority; break;
              case 'selectedForToMeetingDescusion': rowData[columnDef.title] = initiative.selectedForTOMeeting ? 'Yes' : 'No'; break;
              case 'workstreamLeadInfo': rowData[columnDef.title] = processPersonArray(initiative.workstreamLeadInfo); break;
              case 'workstreamSponsorInfo': rowData[columnDef.title] = processPersonArray(initiative.workstreamSponsorInfo); break;
              case 'workstreamLeadSubworkstream': rowData[columnDef.title] = processSubworkstreamArray(initiative.workstreamLeadSubworkstream); break;
              case 'netRecurringBenefit': rowData[columnDef.title] = calculateNetRecurringBenefit(initiative) || 0; break;
              case 'oneTimeBenefit': rowData[columnDef.title] = calculateOneTimeBenefit(initiative) || 0; break;
              case 'oneTimeImplementationCost': rowData[columnDef.title] = calculateOneTimeImplementationCost(initiative) || 0; break;

              // Dates using formatDateSafely - NOW RETURNING EXCEL SERIAL NUMBERS
              case 'L3 planned date': rowData[columnDef.title] = formatDateSafely(L3PlanDate(initiative)); break;
              case 'L4 planned date': rowData[columnDef.title] = formatDateSafely(L4PlanDate(initiative)); break;
              case 'L2 planned date': rowData[columnDef.title] = formatDateSafely(L2PlanDate(initiative)); break;
              case 'L5 planned date': rowData[columnDef.title] = formatDateSafely(L5PlanDate(initiative)); break;
              case 'L0 actual date': rowData[columnDef.title] = formatDateSafely(L0PlanDate(initiative)); break;
              case 'L1 actual date': rowData[columnDef.title] = formatDateSafely(L1PlanDate(initiative)); break;
              case 'L2 actual date': rowData[columnDef.title] = formatDateSafely(L2ActualDate(initiative)); break;
              case 'L3 actual date': rowData[columnDef.title] = formatDateSafely(L3ActualDate(initiative)); break;
              case 'L4 actual date': rowData[columnDef.title] = formatDateSafely(L4ActualDate(initiative)); break;
              case 'L5 actual date': rowData[columnDef.title] = formatDateSafely(L5ActualDate(initiative)); break;

              // Original planned dates
              case 'L1 Original Planned Date': rowData[columnDef.title] = formatDateSafely(L1OriginalPlanDate(initiative)); break;
              case 'L2 Original Planned Date': rowData[columnDef.title] = formatDateSafely(L2OriginalPlanDate(initiative)); break;
              case 'L3 Original Planned Date': rowData[columnDef.title] = formatDateSafely(L3OriginalPlanDate(initiative)); break;
              case 'L4 Original Planned Date': rowData[columnDef.title] = formatDateSafely(L4OriginalPlanDate(initiative)); break;
              case 'L5 Original Planned Date': rowData[columnDef.title] = formatDateSafely(L5OriginalPlanDate(initiative)); break;

              // Submitted For Approval Dates (from history)
              case 'Sub . for L1 approv . date': rowData[columnDef.title] = formatDateSafely(SubmittedForL1PlanDate(initiative, historyData)); break;
              case 'Sub . for L2 approv . date': rowData[columnDef.title] = formatDateSafely(SubmittedForL2PlanDate(initiative, historyData)); break;
              case 'Sub . for L3 approv . date': rowData[columnDef.title] = formatDateSafely(SubmittedForL3PlanDate(initiative, historyData)); break;
              case 'Sub . for L4 approv . date': rowData[columnDef.title] = formatDateSafely(SubmittedForL4PlanDate(initiative, historyData)); break;
              case 'Sub . for L5 approv . date': rowData[columnDef.title] = formatDateSafely(SubmittedForL5PlanDate(initiative, historyData)); break;
              case 'latestActualDateAllLGates': rowData[columnDef.title] = formatDateSafely(LLatestActualDate(initiative, historyData)); break;

              case 'Planning summary':
                const planningSummary = InitiativePlanningSummary(initiative);
                rowData[columnDef.title] = planningSummary.planningStatus; break;
              case 'Planning summary simplified':
                rowData[columnDef.title] = planningSummarySimplified(initiative) || '';
                break;
              case 'Workstream lead pending approval': rowData[columnDef.title] = checkWorkstreamLeadApprovalStatus(initiative, historyData); break;
              case 'Workstream sponsor pending approval': rowData[columnDef.title] = checkWorkstreamSponsorApprovalStatus(initiative, historyData); break;
              case 'TO finance pending approval': rowData[columnDef.title] = checkToFinanceApprovalStatus(initiative, historyData); break;
              case 'Strategy analyst pending approval': rowData[columnDef.title] = checkStrategyAnalystApprovalStatus(initiative, historyData); break;
              case 'Net recurring benefit range': rowData[columnDef.title] = getNetRecurringBenefitCategory(initiative); break;
              case 'Future implementation costs range': rowData[columnDef.title] = getImplementationCostCatagory(initiative); break;

              case 'Pipeline weekly variance (L0+)': rowData[columnDef.title] = getNetRecurringBenefitChangeCategoryL0Plus(initiative, selectedRange); break;
              case 'Pipeline weekly variance (L1+)': rowData[columnDef.title] = getNetRecurringBenefitChangeCategoryL1Plus(initiative, selectedRange); break;
              case 'Pipeline weekly variance (L2+)': rowData[columnDef.title] = getNetRecurringBenefitChangeCategoryL2Plus(initiative, selectedRange); break;
              case 'Pipeline weekly variance (L3+)': rowData[columnDef.title] = getNetRecurringBenefitChangeCategoryL3Plus(initiative, selectedRange); break;
              case 'Pipeline weekly variance (L4+)': rowData[columnDef.title] = getNetRecurringBenefitChangeCategoryL4Plus(initiative, selectedRange); break;

              case 'Actual KPI update check (for last completed and previous period)': rowData[columnDef.title] = sortInitiativesByActuals(initiative, 'actualKpiUpdateCheck'); break;
              case 'Actual impact update check (for last completed and previous period)': rowData[columnDef.title] = sortInitiativesByActuals(initiative, 'actualImpactUpdateCheck'); break;
              case '(FY23-24) Actual impact update check': rowData[columnDef.title] = groupInitiativesByYearActual(initiative, '(FY23-24) Actual impact update check'); break;
              case '(FY24-25) Actual impact update check': rowData[columnDef.title] = groupInitiativesByYearActual(initiative, '(FY24-25) Actual impact update check'); break;

              case 'L3 date compliance check (forecast vs planned date)': rowData[columnDef.title] = getComplianceStatus(initiative, 'L3').join(', '); break;
              case 'L4 date compliance check (forecast vs planned date)': rowData[columnDef.title] = getComplianceStatus(initiative, 'L4').join(', '); break;

              case 'Number of Attachment': rowData[columnDef.title] = initiative.attachmentCount || 0; break;
              case 'Has qualitative KPI?':
                const hasQuantitativeKPI = getQualitativeKPI(initiative);
                rowData[columnDef.title] = hasQuantitativeKPI ? 'No' : 'Yes';
                break;
              case 'initiativeNumber - initiativeName': rowData[columnDef.title] = `${initiative.initiativeNumber || ''} - ${initiative.initiativeName || ''}`; break;
              case 'Cancelation Date':
                rowData[columnDef.title] = formatDateSafely(getCancellationDate(initiative));
                break;
              case 'Created by':
                rowData[columnDef.title] = getLatestUserFromHistory(initiative, 'created', 'initiativeNumber', historyData, users);
                break;
              case 'Updated by':
                rowData[columnDef.title] = getLatestUserFromHistory(initiative, 'updated', 'initiativeNumber', historyData, users);
                break;
              case 'Created On':
                rowData[columnDef.title] = initiative.created ? formatDateSafely(initiative.created) : null;
                break;
              case 'Updated On':
                const latestInitiativeUpdateEntry = (initiative.history || [])
                  .filter(entry => entry.action?.includes(`Initiative ${initiative.initiativeNumber} has been updated.`))
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
                rowData[columnDef.title] = latestInitiativeUpdateEntry?.timestamp ? formatDateSafely(latestInitiativeUpdateEntry.timestamp) : null;
                break;

              case 'Description':
                rowData[columnDef.title] = initiative.description || '';
                break;

              // Fallback for direct dataIndex if not handled above
              default:
                if (columnDef.dataIndex && initiative[columnDef.dataIndex] !== undefined) {
                  rowData[columnDef.title] = initiative[columnDef.dataIndex];
                } else {
                  rowData[columnDef.title] = ''; // Default empty if not found or handled
                }
                break;
            }
          });
          return rowData;
        });

        const ws = utils.json_to_sheet(initiativesSheetData);

        // Apply date formatting to show only date (no time)
        applyDateFormats(ws);

        utils.book_append_sheet(wb, ws, 'Initiatives');
      } else {
        console.warn("No initiatives data to download.");
      }

      // --- 3. Milestones Sheet ---
      const allMilestones = initiatives.flatMap(i => i.milestones || []);
      if (allMilestones.length > 0) {
        const processedMilestones = allMilestones.map((milestone) => {
          const parentInitiative = findParentInitiative(milestone, initiatives);
          const ownerUser = users.find((user) => user._id === milestone.Owner);

          const rowData = {};
          milestoneInitialColumns.forEach(columnDef => {
            switch (columnDef.key) {
              case 'milestoneNumber': rowData[columnDef.title] = parseInt(milestone.milestoneNumber) || ''; break;
              case 'initiativeNumber': rowData[columnDef.title] = parseInt(parentInitiative?.initiativeNumber) || ''; break;
              case 'name': rowData[columnDef.title] = milestone.name || ''; break;
              case 'initiativeName': rowData[columnDef.title] = parentInitiative?.initiativeName || ''; break;
              case 'initiativeOwnerInfo': rowData[columnDef.title] = processPersonArray(parentInitiative?.initiativeOwnerInfo); break;
              case 'milestoneNumber - name': rowData[columnDef.title] = `${milestone.milestoneNumber || ''} - ${milestone.name || ''}`; break;
              case 'Owner': rowData[columnDef.title] = ownerUser ? `${ownerUser.firstName || ''} ${ownerUser.lastName || ''}`.trim() : null; break;
              case 'Work starting date': rowData[columnDef.title] = milestone.startDate ? formatDateSafely(milestone.startDate) : null; break;
              case 'Milestone completion date': rowData[columnDef.title] = milestone.endDate ? formatDateSafely(milestone.endDate) : null; break;
              case 'Milestone Status': rowData[columnDef.title] = milestone.status || ''; break;
              case 'Milestone Planning summary': rowData[columnDef.title] = getMilestonePlanningSummary(milestone, parentInitiative) || ''; break;
              case 'Planning summary (simplified)': rowData[columnDef.title] = getSimplifiedPlanningSummary(milestone, parentInitiative) || ''; break;
              case 'type': rowData[columnDef.title] = milestone.type || ''; break;

              case 'Updated On':
                const historyEntries = parentInitiative?.history || [];
                const matchingEntries = historyEntries
                  .filter(entry => entry.action?.includes(`Milestone with Number ${milestone.milestoneNumber} has been updated.`))
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                const latestEntry = matchingEntries[0];
                rowData[columnDef.title] = latestEntry?.timestamp ? formatDateSafely(latestEntry.timestamp) : null;
                break;
              case 'Created On':
                rowData[columnDef.title] = milestone.created ? formatDateSafely(milestone.created) : null;
                break;
              case 'Created by':
                rowData[columnDef.title] = getLatestUserFromHistory(milestone, 'created', 'name', historyData, users);
                break;
              case 'Updated by':
                rowData[columnDef.title] = getLatestUserFromHistory(milestone, 'updated', 'milestoneNumber', historyData, users);
                break;

              case 'planningCheck': rowData[columnDef.title] = milestone.planningCheck || ''; break;
              case 'Change in Milestone completion date (binned, current vs last week)': rowData[columnDef.title] = getMilestoneBinValue(milestone, parentInitiative) || ''; break;
              case 'workstreamSponsorWorkstream': rowData[columnDef.title] = processWorkstreamArray(parentInitiative?.workstreamSponsorWorkstream) || ''; break;
              case 'workstreamLeadSubworkstream': rowData[columnDef.title] = processSubworkstreamArray(parentInitiative?.workstreamLeadSubworkstream) || ''; break;

              default:
                if (columnDef.dataIndex && milestone[columnDef.dataIndex] !== undefined) {
                  rowData[columnDef.title] = milestone[columnDef.dataIndex];
                } else {
                  rowData[columnDef.title] = '';
                }
                break;
            }
          });
          return rowData;
        });

        const ws = utils.json_to_sheet(processedMilestones);
        applyDateFormats(ws);
        utils.book_append_sheet(wb, ws, 'Milestones');
      } else {
        console.warn("No milestone data to download.");
      }

      // --- 4. Impact Sheet (Modified for qualitative impacts) - SINGLE HEADER ROW ---
      const allImpacts = initiatives.flatMap(i => i.impact || []);
      if (allImpacts.length > 0) {
        const impactSheetData = [];

        // Single header row
        const impactHeaderRow = [
          'Initiative Number', 'Workstream', 'Impact Number', 'Frequency and Direction',
          'Type of Impact', 'Accountable Workstream', 'Accountable Subworkstream', 'Plan Annualized Impact', 'Actual Annualized Impact',
          'Forecast Annualized Impact', 'Estimate Annualized Impact', 'Purpose'
        ];

        // Add monthly headers
        dynamicMonths.forEach(month => {
          impactHeaderRow.push(`${month.name}-${month.year}`);
        });

        // Add yearly headers
        yearsForHeaders.forEach(year => {
          impactHeaderRow.push(`Year ${year}/${year + 1}`);
        });

        // Add quarterly headers
        quartersForHeaders.forEach(quarter => {
          const [qPrefix, qYear] = quarter.split('-');
          impactHeaderRow.push(`${qPrefix}-${qYear}/${parseInt(qYear) + 1}`);
        });

        impactSheetData.push(impactHeaderRow);

        allImpacts.forEach((impact) => {
          const parentInitiative = findParentInitiative(impact, initiatives);
          let subworkstreamName = parentInitiative?.workstreamLeadSubworkstream?.[0]?.subworkstreamName || '';

          // Determine which purpose types to include based on impact type
          let purposeTypes = ['Plan', 'Actual', 'Forecast', 'Estimate'];
          if (impact.typeOfImpact === 'Qualitative Impact') {
            purposeTypes = ['Plan', 'Actual']; // Only show Plan and Actual for qualitative impacts
          }

          purposeTypes.forEach(type => {
            const row = new Array(impactHeaderRow.length).fill('');
            row[0] = parseInt(parentInitiative?.initiativeNumber) || '';
            row[1] = subworkstreamName;
            row[2] = parseInt(impact.impactNumber) || '';
            row[3] = impact.frequencyAndDirection || '';
            row[4] = impact.typeOfImpact || '';
            row[5] = parentInitiative.workstreamSponsorWorkstream?.[0]?.workstreamName || '';
            row[6] = parentInitiative.workstreamLeadSubworkstream?.[0]?.subworkstreamName || '';
            // Use qualitative values for qualitative impacts, numeric values for others
            if (impact.typeOfImpact === 'Qualitative Impact') {
              row[7] = impact.annualizedValues?.planqualitativeimpact || '';
              row[8] = impact.annualizedValues?.actualqualitativeimpact || '';
              row[9] = ''; // No forecast for qualitative
              row[10] = ''; // No estimate for qualitative
            } else {
              row[7] = impact.annualizedValues?.plan || '';
              row[8] = impact.annualizedValues?.actual || '';
              row[9] = impact.annualizedValues?.forecast || '';
              row[10] = impact.annualizedValues?.estimate || '';
            }

            row[11] = type;

            let currentDataColIndex = 12;
            dynamicMonths.forEach((month, monthIdx) => {
              let value = '';
              if (impact.typeOfImpact === 'Qualitative Impact' || !parentInitiative.hasFinancialImpact) {
                if (type === 'Plan') { value = impact.monthlyPlanQualitativeImpact?.[monthIdx] ?? ''; }
                else if (type === 'Actual') { value = impact.monthlyActualQualitativeImpact?.[monthIdx] ?? ''; }
                // No forecast/estimate for qualitative
              } else {
                if (type === 'Plan') { value = impact.monthlyPlan?.[monthIdx] ?? ''; }
                else if (type === 'Actual') { value = impact.monthlyActual?.[monthIdx] ?? ''; }
                else if (type === 'Forecast') { value = impact.monthlyForecast?.[monthIdx] ?? ''; }
                else if (type === 'Estimate') { value = impact.monthlyEstimate?.[monthIdx] ?? ''; }
              }
              row[currentDataColIndex++] = value;
            });

            yearsForHeaders.forEach((year, yearIdx) => {
              let value = '';
              if (impact.typeOfImpact === 'Qualitative Impact') {
                if (type === 'Plan') { value = impact.annuallyPlanQualitativeImpact?.[yearIdx] ?? ''; }
                else if (type === 'Actual') { value = impact.annuallyActualQualitativeImpact?.[yearIdx] ?? ''; }
                // No forecast/estimate for qualitative
              } else {
                if (type === 'Plan') { value = impact.annuallyPlan?.[yearIdx] ?? ''; }
                else if (type === 'Actual') { value = impact.annuallyActual?.[yearIdx] ?? ''; }
                else if (type === 'Forecast') { value = impact.annuallyForecast?.[yearIdx] ?? ''; }
                else if (type === 'Estimate') { value = impact.annuallyEstimate?.[yearIdx] ?? ''; }
              }
              row[currentDataColIndex++] = value;
            });

            quartersForHeaders.forEach((quarter, quarterIdx) => {
              let value = '';
              if (impact.typeOfImpact === 'Qualitative Impact') {
                if (type === 'Plan') { value = impact.quarterlyPlanQualitativeImpact?.[quarterIdx] ?? ''; }
                else if (type === 'Actual') { value = impact.quarterlyActualQualitativeImpact?.[quarterIdx] ?? ''; }
                // No forecast/estimate for qualitative
              } else {
                if (type === 'Plan') { value = impact.quarterlyPlan?.[quarterIdx] ?? ''; }
                else if (type === 'Actual') { value = impact.quarterlyActual?.[quarterIdx] ?? ''; }
                else if (type === 'Forecast') { value = impact.quarterlyForecast?.[quarterIdx] ?? ''; }
                else if (type === 'Estimate') { value = impact.quarterlyEstimate?.[quarterIdx] ?? ''; }
              }
              row[currentDataColIndex++] = value;
            });

            impactSheetData.push(row);
          });
        });
        const impactSheet = utils.aoa_to_sheet(impactSheetData);
        utils.book_append_sheet(wb, impactSheet, 'Impact');
      } else {
        console.warn("No impact data to download.");
      }

      // --- 5. KPI Sheet (Modified for non-financial KPIs) - SINGLE HEADER ROW ---
      const allKPIs = initiatives.flatMap(i => i.KPI || []);
      if (allKPIs.length > 0) {
        const kpiSheetData = [];

        // Single header row
        const kpiHeaderRow = [
          'Initiative Number', 'KPINumber', 'StartAmortizationOrDepreciation', 'TypeOfKPI',
          'UniversalKPI', 'unitOfMeasurement', 'aggregationMode', 'isFinancial', 'Accountable Workstream',
          'Accountable Subworkstream', 'Baseline', 'Purpose'
        ];

        // Add monthly headers
        dynamicMonths.forEach(month => {
          kpiHeaderRow.push(`${month.name}-${month.year}`);
        });

        // Add yearly headers
        yearsForHeaders.forEach(year => {
          kpiHeaderRow.push(`Year ${year}/${year + 1}`);
        });

        // Add quarterly headers
        quartersForHeaders.forEach(quarter => {
          const [qPrefix, qYear] = quarter.split('-');
          kpiHeaderRow.push(`${qPrefix}-${qYear}/${parseInt(qYear) + 1}`);
        });

        kpiSheetData.push(kpiHeaderRow);

        allKPIs.forEach((kpi) => {
          const parentInitiative = findParentInitiative(kpi, initiatives);

          // Determine which purpose types to include based on isFinancial flag
          let purposeTypes = ['Plan', 'Actual', 'Forecast', 'Estimate'];
          if (kpi.isFinancial === false) {
            purposeTypes = ['Plan', 'Actual']; // Only show Plan and Actual for non-financial KPIs
          }

          purposeTypes.forEach(type => {
            const row = new Array(kpiHeaderRow.length).fill('');
            let currentDataColIndex = 0;

            row[currentDataColIndex++] = parseInt(parentInitiative?.initiativeNumber) || '';
            row[currentDataColIndex++] = parseInt(kpi.KPINumber) || '';
            row[currentDataColIndex++] = kpi.StartAmortizationOrDepreciation || '';
            row[currentDataColIndex++] = kpi.TypeOfKPI || '';
            row[currentDataColIndex++] = kpi.UniversalKPI || '';
            row[currentDataColIndex++] = kpi.unitOfMeasurement || '';
            row[currentDataColIndex++] = kpi.aggregationMode || '';
            row[currentDataColIndex++] = kpi.isFinancial !== undefined ? (kpi.isFinancial ? 'Yes' : 'No') : '';
            row[currentDataColIndex++] = parentInitiative.workstreamSponsorWorkstream?.[0]?.workstreamName || '';
            row[currentDataColIndex++] = parentInitiative.workstreamLeadSubworkstream?.[0]?.subworkstreamName || '';
            row[currentDataColIndex++] = kpi.baseline || '';
            row[currentDataColIndex++] = type;

            dynamicMonths.forEach((month, monthIdx) => {
              let value = '';
              if (kpi.isFinancial === false) {
                // Use non-financial KPI string values
                if (type === 'Plan') { value = kpi.monthlynonFinancialKPIPlan?.[monthIdx] ?? ''; }
                else if (type === 'Actual') { value = kpi.monthlynonFinancialKPIActual?.[monthIdx] ?? ''; }
                // No forecast/estimate for non-financial KPIs
              } else {
                // Use financial KPI numeric values
                if (type === 'Plan') { value = kpi.monthlyPlan?.[monthIdx] ?? ''; }
                else if (type === 'Actual') { value = kpi.monthlyActual?.[monthIdx] ?? ''; }
                else if (type === 'Forecast') { value = kpi.monthlyForecast?.[monthIdx] ?? ''; }
                else if (type === 'Estimate') { value = kpi.monthlyEstimate?.[monthIdx] ?? ''; }
              }
              row[currentDataColIndex++] = value;
            });

            yearsForHeaders.forEach((year, yearIdx) => {
              let value = '';
              if (kpi.isFinancial === false) {
                if (type === 'Plan') { value = kpi.annuallynonFinancialKPIPlan?.[yearIdx] ?? ''; }
                else if (type === 'Actual') { value = kpi.annuallynonFinancialKPIActual?.[yearIdx] ?? ''; }
                // No forecast/estimate for non-financial KPIs
              } else {
                if (type === 'Plan') { value = kpi.annuallyPlan?.[yearIdx] ?? ''; }
                else if (type === 'Actual') { value = kpi.annuallyActual?.[yearIdx] ?? ''; }
                else if (type === 'Forecast') { value = kpi.annuallyForecast?.[yearIdx] ?? ''; }
                else if (type === 'Estimate') { value = kpi.annuallyEstimate?.[yearIdx] ?? ''; }
              }
              row[currentDataColIndex++] = value;
            });

            quartersForHeaders.forEach((quarter, quarterIdx) => {
              let value = '';
              if (kpi.isFinancial === false) {
                if (type === 'Plan') { value = kpi.quarterlynonFinancialKPIPlan?.[quarterIdx] ?? ''; }
                else if (type === 'Actual') { value = kpi.quarterlynonFinancialKPIActual?.[quarterIdx] ?? ''; }
                // No forecast/estimate for non-financial KPIs
              } else {
                if (type === 'Plan') { value = kpi.quarterlyPlan?.[quarterIdx] ?? ''; }
                else if (type === 'Actual') { value = kpi.quarterlyActual?.[quarterIdx] ?? ''; }
                else if (type === 'Forecast') { value = kpi.quarterlyForecast?.[quarterIdx] ?? ''; }
                else if (type === 'Estimate') { value = kpi.quarterlyEstimate?.[quarterIdx] ?? ''; }
              }
              row[currentDataColIndex++] = value;
            });

            kpiSheetData.push(row);
          });
        });

        const kpiSheet = utils.aoa_to_sheet(kpiSheetData);
        utils.book_append_sheet(wb, kpiSheet, 'KPI');
      } else {
        console.warn("No KPI data to download.");
      }

      // --- 6. Attachment Sheet ---
      const allAttachments = initiatives.flatMap(initiative => {
        if (!Array.isArray(initiative.attachment) || initiative.attachment.length === 0) return [];

        return initiative.attachment.map(att => {
          // --- 💡 Modification Starts Here ---
          // 1. Get the attachment string, defaulting to "".
          const fullPath = att.attachment ?? "";
          // 2. Remove the trailing comma and space if present (based on your example output).
          const pathWithoutTrailingChar = fullPath.replace(/, $/, '');
          // 3. Split the string by the path separator '/' and get the last element (the filename).
          const filename = pathWithoutTrailingChar.split('/').pop();
          // --- 💡 Modification Ends Here ---

          return {
            initiativeNumber: parseInt(initiative.initiativeNumber) ?? "",
            initiativeName: initiative.initiativeName ?? "",
            attachment: filename ?? "", // Use the extracted filename here
            typeOfAttachment: att.typeOfAttachment ?? "",
          };
        });
      });

      // console.log(✅ Found ${allAttachments.length} attachments to process.);

      if (allAttachments.length > 0) {
        const sheetData = [
          ["initiativeNumber", "initiativeName", "attachment", "typeOfAttachment"],
          ...allAttachments.map(att => [
            parseInt(att.initiativeNumber),
            att.initiativeName,
            att.attachment, // This now contains only the filename
            att.typeOfAttachment,
          ]),
        ];

        utils.book_append_sheet(wb, utils.aoa_to_sheet(sheetData), "Attachments");
        console.log("📎 'Attachments' sheet appended successfully.");
      } else {
        console.warn("⚠️ No attachment data to download.");
        utils.book_append_sheet(wb, utils.aoa_to_sheet([["No attachment data found"]]), "Attachments");
      }

      // 7. Business Case Sheet
      const allBusinessCases = initiatives
        .filter(initiative => initiative?.businessCase && typeof initiative.businessCase === "object")
        .map(initiative => {
          const businessCase = initiative.businessCase;

          return {
            initiativeNumber: parseInt(initiative.initiativeNumber) ?? "",
            initiativeName: initiative.initiativeName ?? "", // FIXED: Don't parse initiativeName as integer
            baselineValue: businessCase.baselineValue ?? "",
            baselineMeasureAndUnit: businessCase.baselineMeasureAndUnit ?? "",
            baselineTargetValue: businessCase.baselineTargetValue ?? "",
            implementationCosts: businessCase.implementationCosts ?? "",
            keyAssumptions: businessCase.keyAssumptions ?? "",
            MoneyStepAndMeasureSuccess: businessCase.MoneyStepAndMeasureSuccess ?? "",
            NetRecuringBenefit: businessCase.NetRecuringBenefit ?? "",
            oneTimeBenefit: businessCase.oneTimeBenefit ?? ""
          };
        });

      // Prepare data for aoa_to_sheet (Array of Arrays)
      let sheetData = [];

      // Add header row
      sheetData.push([
        "initiativeNumber",
        "initiativeName",
        "baselineValue",
        "baselineMeasureAndUnit",
        "baselineTargetValue",
        "implementationCosts",
        "keyAssumptions",
        "MoneyStepAndMeasureSuccess",
        "NetRecuringBenefit",
        "oneTimeBenefit"
      ]);

      // Add data rows
      if (allBusinessCases.length > 0) {
        allBusinessCases.forEach(bc => {
          sheetData.push([
            bc.initiativeNumber,
            bc.initiativeName,
            bc.baselineValue,
            bc.baselineMeasureAndUnit,
            bc.baselineTargetValue,
            bc.implementationCosts,
            bc.keyAssumptions,
            bc.MoneyStepAndMeasureSuccess,
            bc.NetRecuringBenefit,
            bc.oneTimeBenefit
          ]);
        });
      } else {
        // Add an empty placeholder row if no data
        sheetData.push(["No business case data found"]);
      }

      // Append Business Case sheet
      utils.book_append_sheet(wb, utils.aoa_to_sheet(sheetData), "Business Cases");

      // --- 8. Timeline Sheet ---
      const allTimelines = initiatives.flatMap(i => i.timeline || []);
      if (allTimelines.length > 0) {
        const timelineSheetData = allTimelines.map((timeline) => {
          const parentInitiative = findParentInitiative(timeline, initiatives);
          return {
            'Stage': timeline.stage || '',
            'Plan Date': timeline.date?.plan ? formatDateSafely(timeline.date.plan) : null,
            'Actual Date': timeline.date?.actual ? formatDateSafely(timeline.date.actual) : null,
            'Forecast Date': timeline.date?.forecast ? formatDateSafely(timeline.date.forecast) : null,
            'Estimate Date': timeline.date?.estimate ? formatDateSafely(timeline.date.estimate) : null,
            'Initiative Number': parseInt(parentInitiative?.initiativeNumber) || '',
          };
        });

        const ws = utils.json_to_sheet(timelineSheetData);
        applyDateFormats(ws);
        utils.book_append_sheet(wb, ws, 'Timeline');
      } else {
        console.warn("No timeline data to download.");
      }

      // --- 9. Approval Summary Sheet ---
      if (initiatives && initiatives.length > 0) {
        const approvalSummarySheetData = initiatives.map((initiative) => {
          const { _id, approvalSummary, initiativeNumber, hasFinancialImpact, workstreamLeadInfo, workstreamSponsorInfo } = initiative;
          const approvalData = {};
          const parsedInitiativeNumber = parseInt(initiativeNumber);
          const toFinanceRoleName = 'TO Finance';
          const strategyAnalystRoleName = 'Strategy Analyst';

          const strategyAnalystUser = users.find((user) => user.role?.name === strategyAnalystRoleName);
          const toFinanceUser = users.find((user) => user.role?.name === toFinanceRoleName);

          const getFullName = (user) => (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '');

          if (approvalSummary) {
            Object.keys(approvalSummary).forEach((level) => {
              const levelData = approvalSummary[level];
              if (levelData) {
                let approvers = [];

                if (levelData.approvalStatus === 'Pending') {
                  if (level === 'L1') {
                    approvers = [processPersonArray(workstreamLeadInfo), getFullName(strategyAnalystUser)];
                  } else if (['L2', 'L3', 'L4', 'L5'].includes(level)) {
                    approvers = [processPersonArray(workstreamLeadInfo), processPersonArray(workstreamSponsorInfo)];
                    if (hasFinancialImpact) {
                      approvers.push(getFullName(toFinanceUser));
                    } else {
                      approvers.push(getFullName(strategyAnalystUser));
                    }
                  }
                } else if (levelData.approvers) {
                  approvers = levelData.approvers.map((approver) => {
                    const user = users.find((u) => u._id === approver.approvedBy);
                    return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Unknown User';
                  });
                }
                const approverNames = approvers.filter(name => name).join(', ');
                approvalData[level] = `${levelData.approvalStatus} (${approverNames})`;
              }
            });
          }
          return { initiativeNumber: parsedInitiativeNumber, ...approvalData };
        });
        utils.book_append_sheet(wb, utils.json_to_sheet(approvalSummarySheetData), 'Approval Summary');
      } else {
        console.warn("No approval summary data to download.");
      }

      // --- 10. Workstream Action Items Sheet ---
      if (actionItems && actionItems.length > 0) {
        const workstreamActionSheetData = actionItems.map((item) => {
          const ownerName = item?.owner ? `${item?.owner.firstName || ''} ${item?.owner.lastName || ''}`.trim() : null;
          const collaboratorNames = (item?.collaborator || [])
            .map(collabUser => `${collabUser.firstName || ''} ${collabUser.lastName || ''}`.trim())
            .filter(Boolean)
            .join(', ');
          const createdByName = item.createdBy ? `${item.createdBy.firstName || ''} ${item.createdBy.lastName || ''}`.trim() : null;
          const subworkstreamName = item?.owner?.subworkstream?.subworkstreamName || '';
          const workstreamName = item?.owner?.subworkstream?.workstream?.workstreamName || item?.owner?.workstream?.workstreamName || '';

          const latestUpdateEvent = getLatestUpdateEvent(item);
          const updatedOnDate = latestUpdateEvent?.eventDate ? formatDateSafely(latestUpdateEvent.eventDate) : null;
          const updatedBy = getLatestUpdateEvent(item);
          const updatedByName = updatedBy?.owner ? `${updatedBy?.owner.firstName} ${updatedBy?.owner.lastName}` : null;

          const rowData = {};
          actionItemInitialColumn.forEach(columnDef => {
            switch (columnDef.key) {
              case 'actionItemNumber': rowData[columnDef.title] = parseInt(item.actionItemNumber) || ''; break;
              case 'workstreamActionName': rowData[columnDef.title] = item.workstreamActionName || item.actionItemNumber || ''; break;
              case 'owner': rowData[columnDef.title] = ownerName; break;
              case 'collaborator': rowData[columnDef.title] = collaboratorNames; break;
              case 'subworkstream': rowData[columnDef.title] = subworkstreamName; break;
              case 'workstream': rowData[columnDef.title] = workstreamName; break;
              case 'selectedForToMeeting': rowData[columnDef.title] = item.selectedForToMeeting ? 'Yes' : 'No'; break;
              case 'status': rowData[columnDef.title] = item.status || ''; break;
              case 'planningSummary': rowData[columnDef.title] = determinePlanningSummary(item) || ''; break;
              case 'comment': rowData[columnDef.title] = item.comment || ''; break;
              case 'dueDate': rowData[columnDef.title] = item.dueDate ? formatDateSafely(item.dueDate) : null; break;
              case 'Created on': rowData[columnDef.title] = item.created ? formatDateSafely(item.created) : null; break;
              case 'Updated on': rowData[columnDef.title] = updatedOnDate; break;
              case 'createdBy': rowData[columnDef.title] = createdByName; break;
              case 'updatedBy': rowData[columnDef.title] = updatedByName; break;
              default:
                // Handles cases where dataIndex is a simple string but not in the switch
                if (columnDef.dataIndex && typeof columnDef.dataIndex === 'string' && item[columnDef.dataIndex] !== undefined) {
                  rowData[columnDef.title] = item[columnDef.dataIndex];
                } else {
                  rowData[columnDef.title] = '';
                }
                break;
            }
          });
          return rowData;
        });

        const ws = utils.json_to_sheet(workstreamActionSheetData);
        applyDateFormats(ws);
        utils.book_append_sheet(wb, ws, 'Workstream Action Items');
      } else {
        console.warn("No workstream action items data to download.");
      }

      // --- 11. Subworkstreams Sheet (Fixed naming) ---
      if (subworkstreams && subworkstreams.length > 0) {
        const subworkstreamsSheetData = subworkstreams.map((sw) => {
          const { _id, workstream, ...rest } = sw;
          const parentWorkstreamName = workstream ? workstream.workstreamName : '';
          return { ...rest, workstream: parentWorkstreamName };
        });
        utils.book_append_sheet(wb, utils.json_to_sheet(subworkstreamsSheetData), 'Subworkstreams');
      } else {
        console.warn("No subworkstreams data to download.");
      }

      // --- 12. Workstreams Sheet (Fixed naming) ---
      if (workstreams && workstreams.length > 0) {
        const workstreamsSheetData = workstreams.map((ws) => {
          const { _id, ...rest } = ws;
          return rest;
        });
        utils.book_append_sheet(wb, utils.json_to_sheet(workstreamsSheetData), 'Workstreams');
      } else {
        console.warn("No workstreams data to download.");
      }

      // --- Final Write Operation ---
      if (wb.SheetNames.length > 0) {
        writeFile(wb, 'AllData.xlsx', { cellDates: false }); // Set cellDates to false since we're using serial numbers
      } else {
        console.warn("No sheets were added to the workbook. Nothing to download.");
      }

    } catch (error) {
      console.error("Error during file download:", error);
    }
  }, [users, initiatives, subworkstreams, workstreams, actionItems, historyData, selectedRange]); // All dependencies

  return handleDownload;
};